/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams, HttpRequest} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiResponse} from '../interfaces/ApiResponse';
import {Environment} from '../interfaces/Environment';
import {SortPipe} from '../pipes/sort.pipe';
import {TuiDay, TuiDayRange} from '@taiga-ui/cdk'
import {UntypedFormGroup} from '@angular/forms'

export type FileUpload = {
  event: HttpEvent<any>,
  progress?: number,
  response?: ApiResponse<any>,
};

@Injectable({
  providedIn: 'root',
})
export class CompoApiService {
  private readonly options = {};
  private readonly headers: HttpHeaders;
  environment = inject(Environment);

  constructor(private readonly domSanitizer: DomSanitizer, private readonly http: HttpClient) {
    this.headers = new HttpHeaders({'Content-Type': 'application/json', 'ngsw-bypass': 1});
    this.options = {
      headers: this.headers,
      withCredentials: true
    };
  }

  post<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.post<ApiResponse<E>>(
      this.getBaseUrl(url),
      data,
      this.options
    );
  }

  get<T>(url): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(
      this.getBaseUrl(url),
      this.options
    );
  }

  getWithParams<T>(url: string, params: HttpParams): Observable<ApiResponse<T>> {
    const options = {
      headers: this.headers,
      withCredentials: true,
      params
    };

    return this.http.get<ApiResponse<T>>(
      this.getBaseUrl(url),
      options
    );
  }

  delete<T>(url): Observable<ApiResponse<T>> {
    return this.http.delete<ApiResponse<T>>(
      this.getBaseUrl(url),
      this.options
    );
  }

  remove(url) {
    return this.http.delete(
      this.getBaseUrl(url),
      this.options
    );
  }

  patch<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.patch<ApiResponse<E>>(
      this.getBaseUrl(url),
      data,
      this.options
    );
  }

  put<T, E>(url, data: T): Observable<ApiResponse<E>> {
    return this.http.put<ApiResponse<E>>(
      this.getBaseUrl(url),
      data,
      this.options
    );
  }

  head(url) {
    return this.http.head(
      this.getBaseUrl(url),
      this.options
    );
  }

  /**
   * Downloads files via api with token credentials
   * @param url download controller uri
   */
  downloadUrl(url: string): Observable<Blob> {
    const downloadHeaders = new HttpHeaders({'Accept': 'application/octet-stream', 'ngsw-bypass': 1});

    return this.http.get(this.getBaseUrl(url), {headers: downloadHeaders, responseType: 'blob', withCredentials: true});
  }

  private getBaseUrl(url: string) {
    if (url?.indexOf('/assets') === 0) {
      return url;
    }

    return url.indexOf('http') !== 0 ? this.environment.serverApi + String(url) : url;
  }

  downloadPostUrl<T>(url: string, data: T): Observable<Blob> {
    const downloadHeaders = new HttpHeaders({'Accept': 'application/octet-stream', 'ngsw-bypass': 1});

    return this.http.post(this.getBaseUrl(url), data, {headers: downloadHeaders, responseType: 'blob', withCredentials: true});
  }

  /**
   * Post request as multipart form data
   * @param url
   * @param data
   */
  postForm<T, E>(url, data: T) {
    return this.http.post<ApiResponse<E>>(
      this.getBaseUrl(url),
      this.buildFormData(data),
      {
        withCredentials: true,
        reportProgress: true,
        headers: new HttpHeaders({'ngsw-bypass': 1}),
      }
    );
  }

  postFormWithProgress<T, E>(url: string, data: T): Observable<FileUpload> {

    const req = new HttpRequest(
      'POST',
      this.getBaseUrl(url),
      this.buildFormData(data),
      {
        reportProgress: true,
        withCredentials: true,
        headers: new HttpHeaders({'ngsw-bypass': 1}),
      }
    );

    return this.http.request(req).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / (event.total || 1));
            return {
              progress: progress,
              event: event
            } as FileUpload;

          case HttpEventType.Response:
            return {
              response: event.body as ApiResponse<E>,
              event: event,
            } as FileUpload;

          default:
            return {
              event: event,
            } as FileUpload;
        }
      })
    );
  }

  putForm<T, E>(url, data: T) {
    return this.http.put<ApiResponse<E>>(
      this.getBaseUrl(url),
      this.jsonToFormData(data),
      {
        withCredentials: true,
        headers: new HttpHeaders({'ngsw-bypass': 1}),
      }
    );
  }

  /**
   * Method is useful when it's necessary to submit a form (not a JSON object) to server
   * @param formValue
   */
  private buildFormData(formValue): FormData {

    const formData = new FormData();

    for (const key of Object.keys(formValue)) {

      const value = formValue[key];
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    return formData;
  }

  private getFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach(key => {
        this.getFormData(formData, data[key], parentKey ? `${parentKey}.${key}` : key);
      });
    } else {
      const value = data == null ? '' : data;

      formData.append(parentKey, value);
    }
  }

  private jsonToFormData(data) {
    const formData = new FormData();

    this.getFormData(formData, data, false);

    return formData;
  }

  getDocumentImageBlob(url: string): Observable<SafeUrl> {
    return this.http
      .get(this.getBaseUrl(url), {
        responseType: 'blob',
        withCredentials: true
      }).pipe(map(e => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(e))));
  }

  naturalSort(arr: any[], field: string = 'header'): any[] {
    return SortPipe.naturalSort(arr, field);
  }

  convertDate(date: Date): string {
    const formatter = new Intl.DateTimeFormat(this.environment.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find(part => part.type === type).value;

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
  }

  convertDateTime(date: Date): string {
    const formatter = new Intl.DateTimeFormat(this.environment.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find(part => part.type === type).value;

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
  }

  endOfToday(): Date {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  }

  startOfMonth(date?: Date): Date {
    const targetDate = date || new Date();
    return new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
  }

  getDateRange(params: HttpParams): TuiDayRange {
    const dateStart = params.get('dateStart');
    const dateEnd = params.get('dateEnd');

    if (dateStart && dateEnd) {
      const from = new Date(dateStart);
      const to = new Date(dateEnd);

      return new TuiDayRange(
        new TuiDay(from.getFullYear(), from.getMonth(), from.getDate()),
        new TuiDay(to.getFullYear(), to.getMonth(), to.getDate())
      );
    }
  }

  focusFirstError(model: UntypedFormGroup, $event: PointerEvent) {
    model.updateValueAndValidity();
    model.markAllAsTouched();

    const form = ($event.target as HTMLElement).closest('form');
    const invalidControl = form.querySelector('.ng-invalid') || form.querySelector('.invalid-feedback');

    if (invalidControl) {
      invalidControl.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  flashElement(element: HTMLElement): void {
    element.classList.add('highlight-flash');
    const onAnimationEnd = () => {
      element.classList.remove('highlight-flash');
      element.removeEventListener('animationend', onAnimationEnd);
    };
    element.addEventListener('animationend', onAnimationEnd);
  }
}
